import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { OpenStandaloneButton } from './OpenStandaloneButton';
import { DossierSearchPalette } from '../dossiers/DossierSearchPalette';
import { BreadcrumbNav } from './BreadcrumbNav';
import { NotificationBell } from './NotificationBell';
import { UserMenu } from './UserMenu';
import { FloatingTimer } from '../timeTracking/FloatingTimer';
import { resolvePageMeta } from './pageMeta';
import { usePageSettingsStore } from '../../store/usePageSettingsStore';
import { TabSettingsDialog } from '../ui/TabSettingsButton';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/Tooltip';

interface AppHeaderProps {
  onOpenCreate?: () => void;
}

export function AppHeader({ onOpenCreate }: AppHeaderProps) {
  const { pathname } = useLocation();
  const meta = resolvePageMeta(pathname);
  const Icon = meta?.icon;

  const tabSettings = usePageSettingsStore((s) => s.tabSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <header className="grid grid-cols-[minmax(200px,1fr)_auto_minmax(max-content,1fr)] items-center gap-4 h-[76px] px-8 bg-white border-b border-neutral-200 flex-shrink-0 print:hidden">
      <div className="flex flex-col min-w-0 gap-px">
        <BreadcrumbNav />
        {meta ? (
          <>
            <div className="flex items-center gap-2.5 min-w-0">
              {Icon ? (
                <Icon
                  className="w-5 h-5 text-cime-primary flex-shrink-0"
                  aria-hidden
                />
              ) : null}
              <h1
                className="text-xl leading-7 font-semibold text-cime-text-primary truncate"
                style={{ fontFamily: 'var(--cime-font-heading)' }}
              >
                {meta.title}
              </h1>
            </div>
            {meta.subtitle ? (
              <p className="text-[13px] leading-[18px] text-cime-text-muted truncate">
                {meta.subtitle}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <DossierSearchPalette />
        {onOpenCreate ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onOpenCreate}
                aria-label="Créer"
                className="w-9 h-9 flex items-center justify-center rounded-full text-cime-primary hover:text-[#ea7549] transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  className="w-6 h-6"
                  fill="currentColor"
                  aria-hidden
                >
                  {/* Font Awesome Pro 6.5.1 — circle-plus regular */}
                  <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344c0 13.3 10.7 24 24 24s24-10.7 24-24V280h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H280V168c0-13.3-10.7-24-24-24s-24 10.7-24 24v64H168c-13.3 0-24 10.7-24 24s10.7 24 24 24h64v64z" />
                </svg>
              </button>
            </TooltipTrigger>
            <TooltipContent>Créer</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      {/* Ordre fixe : réglage, nouvel onglet, chrono, cloche, avatar. Les deux
          premiers ont une place réservée, les trois autres ne bougent jamais. */}
      <div className="flex items-center gap-1.5 justify-end min-w-0">
        {/* Roue toujours présente : grisée et inerte sur une page sans réglage, pour que l'en-tête ne bouge pas. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={tabSettings ? () => setSettingsOpen(true) : undefined}
              aria-label="Réglage de la page"
              aria-disabled={!tabSettings}
              className={
                tabSettings
                  ? 'w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors'
                  : 'w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-neutral-300 cursor-default'
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="w-[18px] h-[18px]"
                fill="currentColor"
                aria-hidden
              >
                {/* Font Awesome Pro 6.5.1 — gear regular */}
                <path
                  fill="currentColor"
                  d="M256 0c17 0 33.6 1.7 49.8 4.8c7.9 1.5 21.8 6.1 29.4 20.1c2 3.7 3.6 7.6 4.6 11.8l9.3 38.5C350.5 81 360.3 86.7 366 85l38-11.2c4-1.2 8.1-1.8 12.2-1.9c16.1-.5 27 9.4 32.3 15.4c22.1 25.1 39.1 54.6 49.9 86.3c2.6 7.6 5.6 21.8-2.7 35.4c-2.2 3.6-4.9 7-8 10L459 246.3c-4.2 4-4.2 15.5 0 19.5l28.7 27.3c3.1 3 5.8 6.4 8 10c8.2 13.6 5.2 27.8 2.7 35.4c-10.8 31.7-27.8 61.1-49.9 86.3c-5.3 6-16.3 15.9-32.3 15.4c-4.1-.1-8.2-.8-12.2-1.9L366 427c-5.7-1.7-15.5 4-16.9 9.8l-9.3 38.5c-1 4.2-2.6 8.2-4.6 11.8c-7.7 14-21.6 18.5-29.4 20.1C289.6 510.3 273 512 256 512s-33.6-1.7-49.8-4.8c-7.9-1.5-21.8-6.1-29.4-20.1c-2-3.7-3.6-7.6-4.6-11.8l-9.3-38.5c-1.4-5.8-11.2-11.5-16.9-9.8l-38 11.2c-4 1.2-8.1 1.8-12.2 1.9c-16.1 .5-27-9.4-32.3-15.4c-22-25.1-39.1-54.6-49.9-86.3c-2.6-7.6-5.6-21.8 2.7-35.4c2.2-3.6 4.9-7 8-10L53 265.7c4.2-4 4.2-15.5 0-19.5L24.2 218.9c-3.1-3-5.8-6.4-8-10C8 195.3 11 181.1 13.6 173.6c10.8-31.7 27.8-61.1 49.9-86.3c5.3-6 16.3-15.9 32.3-15.4c4.1 .1 8.2 .8 12.2 1.9L146 85c5.7 1.7 15.5-4 16.9-9.8l9.3-38.5c1-4.2 2.6 8.2 4.6 11.8c7.7-14 21.6-18.5 29.4-20.1C222.4 1.7 239 0 256 0zM218.1 51.4l-8.5 35.1c-7.8 32.3-45.3 53.9-77.2 44.6L97.9 120.9c-16.5 19.3-29.5 41.7-38 65.7l26.2 24.9c24 22.8 24 66.2 0 89L59.9 325.4c8.5 24 21.5 46.4 38 65.7l34.6-10.2c31.8-9.4 69.4 12.3 77.2 44.6l8.5 35.1c24.6 4.5 51.3 4.5 75.9 0l8.5-35.1c7.8-32.3 45.3-53.9 77.2-44.6l34.6 10.2c16.5-19.3 29.5-41.7 38-65.7l-26.2-24.9c-24-22.8-24-66.2 0-89l26.2-24.9c-8.5-24-21.5-46.4-38-65.7l-34.6 10.2c-31.8 9.4-69.4-12.3-77.2-44.6l-8.5-35.1c-24.6-4.5-51.3-4.5-75.9 0zM208 256a48 48 0 1 0 96 0 48 48 0 1 0 -96 0zm48 96a96 96 0 1 1 0-192 96 96 0 1 1 0 192z"
                />
              </svg>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {tabSettings
              ? 'Réglage de la page'
              : 'Aucun réglage sur cette page'}
          </TooltipContent>
        </Tooltip>
        {tabSettings ? (
          <TabSettingsDialog
            viewId={tabSettings.viewId}
            tabs={tabSettings.tabs}
            defaultHidden={tabSettings.defaultHidden}
            title={tabSettings.title}
            description={tabSettings.description}
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
          />
        ) : null}
        <OpenStandaloneButton />
        <FloatingTimer />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}
